'use client'

import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'

//__TYPES
interface DestinationData {
  common: any
  branches: any
  promotions: any
  locale: string
}

interface DestinationContextType {
  data: DestinationData | null
  isMobile: boolean
  isLoading: boolean
  hasMounted: boolean
}

interface DestinationProviderProps {
  children: ReactNode
  initialData: DestinationData
}

//__CONTEXT
const DestinationContext = createContext<DestinationContextType | null>(null)

//__HOOK
export const useDestination = () => {
  const context = useContext(DestinationContext)
  if (!context) {
    throw new Error('useDestination must be used within a DestinationProvider')
  }
  return context
}

//__PROVIDER
export const DestinationProvider = ({ children, initialData }: DestinationProviderProps) => {
  //__STATE
  const [data, setData] = useState<DestinationData | null>(initialData)
  const [isMobile, setIsMobile] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasMounted, setHasMounted] = useState(false)

  //__RESPONSIVE HANDLER - OPTIMIZED
  const handleWindowResize = useCallback(() => {
    setIsMobile(window.innerWidth < 768)
  }, [])

  useEffect(() => {
    setHasMounted(true)

    if (typeof window === 'undefined') return

    // Set initial value
    handleWindowResize()

    // Use a debounced resize listener to avoid excessive updates
    let resizeTimeout: NodeJS.Timeout
    const debouncedResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(handleWindowResize, 100)
    }

    window.addEventListener('resize', debouncedResize)
    return () => {
      clearTimeout(resizeTimeout)
      window.removeEventListener('resize', debouncedResize)
    }
  }, [handleWindowResize])

  return (
    <DestinationContext.Provider value={{ data, isMobile, isLoading, hasMounted }}>
      {children}
    </DestinationContext.Provider>
  )
}
